// @givmo/app-admin — sign-in MAIN screen (/login-home). Ports `sign_in_main_screen.dart`.
//
// The entry chooser: "Sign in to admin portal" + a "Sign in with Email" button (→ /sign-in email/pw
// form), the Google + Microsoft provider buttons, and the footer "Don't have an account? Get started"
// (→ /sign-up). Render-only: provider sign-in routes via the @givmo/auth-resolved state; a new
// Firebase user surfaces "User is not found. Please sign up." (the package throws it). Keeps the
// `page-login-home` testid (the guard tests assert it).
//
// AS-WEB2 adds a passkey button to the same stack (`@givmo/passkeys`' `PasskeySignInButton`, lifted
// there by AS-WEB12), rendered ONLY when `usePasskeyAvailability()` says the backend serves the
// assertion routes — so while the backend feature is dark (staging and production today) this screen
// is byte-identical to what it was. This is the one admin surface that offers the passkey SIGN-IN: it
// is where the guard sends every unauthenticated visitor, and a second mount point would double the
// probe traffic without adding a capability. Adding a passkey is not here: a signed-in admin adds one
// on the Sign-in & security page (AS-WEB11; see `agent_docs/auth.md`).

import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate, useSearch } from '@tanstack/react-router';
import { Button, ProviderButton, testIdProp } from '@givmo/design-system';
import {
  secondFactorChallengeOf,
  type AdminProviderId,
  type AdminSignInResult,
  type TotpChallenge,
} from '@givmo/auth';
import { useAuth } from '../../auth/auth-context';
import {
  ESCAPE_HATCH_LINK_CONSOLE,
  EscapeHatchEntryLink,
  OrganizationSsoButton,
  PasskeySignInButton,
  SignInDoorError,
  organizationSsoLabel,
  useOrganizationSsoAvailability,
} from '@givmo/passkeys';
import { AuthShell } from './auth-shell';
import { TotpChallengePrompt } from './totp-challenge';
import { usePasskeyAvailability } from './use-passkey-availability';
import { useAuthError } from './use-auth-error';
import { AUTH_ROUTES, navigationForRoutingState } from './auth-routing';
import { landSignInWithoutCompany } from './companyless-landing';

export function SignInMainScreen() {
  const { signInWithProvider, signInWithPasskey } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { redirect } = useSearch({ from: '/login-home' });
  // Where this door is, for the way back in to return to (AS-WEB26).
  const here = useLocation({ select: (location) => location.href });
  const openEscapeHatch = useCallback(
    () => void navigate({ to: AUTH_ROUTES.lostCompanyAccess, search: { back: here } }),
    [navigate, here],
  );
  // EP-SUITE2-FIX6: a provider sign-IN (and the passkey) signs an account in before it can fail —
  // a new account is rolled back — so the error must outlive the identity boundary's remount.
  const { error, lockRefused, clearError, surface } = useAuthError('login-home');
  const [pendingProvider, setPendingProvider] = useState<AdminProviderId | null>(null);
  const [passkeyPending, setPasskeyPending] = useState(false);
  const [challenge, setChallenge] = useState<TotpChallenge<AdminSignInResult> | null>(null);
  const passkeyAvailable = usePasskeyAvailability();
  // AS-WEB30: the organization door, last in the ways in and only where it can work here (D-AS-48
  // 6); its step lands where this chooser would, and its way back returns here.
  const ssoAvailable = useOrganizationSsoAvailability();
  const openOrganizationSso = useCallback(
    () =>
      void navigate({
        to: AUTH_ROUTES.organizationSso,
        search: { ...(redirect ? { redirect } : {}), back: here },
      }),
    [navigate, redirect, here],
  );
  const organizationSso = ssoAvailable
    ? { label: organizationSsoLabel('signIn'), onOpen: openOrganizationSso }
    : undefined;

  // `navigationForRoutingState` carries `provider: true` for the `unregistered` case so the details
  // screen finishes the provider registration (not the email path), and — for PROVIDER sign-ins —
  // for the `emailUnverified` case, so the OTP screen re-resolves the route after the code confirms
  // (a microsoft.com sign-in lands there before any company exists). Shared by the direct sign-in
  // and the second-factor completion, which resolves to the identical result.
  const goToResolvedDestination = useCallback(
    (result: AdminSignInResult, viaProvider = false) =>
      navigate(navigationForRoutingState(result.state, redirect, { viaProvider })),
    [navigate, redirect],
  );

  const onProvider = useCallback(
    async (provider: AdminProviderId) => {
      clearError();
      setPendingProvider(provider);
      try {
        await goToResolvedDestination(await signInWithProvider(provider), /* viaProvider */ true);
      } catch (thrown) {
        // The OAuth first factor was accepted and a second factor is now owed (AS-WEB1) — prompt for
        // the code rather than reporting a sign-in failure.
        const pending = secondFactorChallengeOf<AdminSignInResult>(thrown);
        if (pending !== null) {
          setChallenge(pending);
        } else {
          surface(thrown);
        }
      } finally {
        setPendingProvider(null);
      }
    },
    [signInWithProvider, goToResolvedDestination, clearError, surface],
  );

  // A passkey failure is an ACTION failure and nothing more: it never signs anyone out, never clears
  // the screen, and leaves the email and provider affordances exactly as they were. `useAuthError`
  // already renders a cancellation as nothing, which is what a dismissed system sheet resolves to.
  const onPasskey = useCallback(async () => {
    clearError();
    setPasskeyPending(true);
    try {
      await goToResolvedDestination(await signInWithPasskey());
    } catch (thrown) {
      // EP-SUITE2-FIX5: a passkey sign-in that found no company is landed, as the email form's is.
      const landing = await landSignInWithoutCompany(thrown, queryClient);
      if (landing !== null) await navigate(landing);
      else surface(thrown);
    } finally {
      setPasskeyPending(false);
    }
  }, [signInWithPasskey, goToResolvedDestination, clearError, surface, queryClient, navigate]);

  if (challenge !== null) {
    return (
      <AuthShell testId="page-login-home">
        <TotpChallengePrompt
          door="login-home"
          challenge={challenge}
          onResolved={(result) => goToResolvedDestination(result, /* viaProvider */ true)}
          onCancel={() => {
            clearError(); // the prompt's error is the door's slot; the chooser starts clean
            setChallenge(null);
          }}
          onUnlink={openEscapeHatch}
          organizationSso={organizationSso}
        />
      </AuthShell>
    );
  }

  return (
    <AuthShell testId="page-login-home">
      <div className="flex flex-col gap-4">
        <h1 className="font-sans text-s22 font-w700 tracking-givmo text-text-strong">
          Sign in to admin portal
        </h1>
        <p className="font-sans text-s14 font-w400 tracking-givmo text-text-secondary">
          Welcome back. Sign in to manage your company's giving program.
        </p>
      </div>

      <div className="flex flex-col gap-12">
        {/* All three auth buttons are the same 40px height (`lg`) for a clean stack. */}
        <Button
          appearance="saas"
          tone="primary"
          size="lg"
          fullWidth
          onClick={() => void navigate({ to: AUTH_ROUTES.signIn })}
          testId="auth-signin-email"
        >
          Sign in with Email
        </Button>

        <div className="flex items-center gap-12 py-4" aria-hidden>
          <span className="h-px flex-1 bg-border-subtle" />
          <span className="font-sans text-s12 font-w500 tracking-normal text-text-tertiary">
            or continue with
          </span>
          <span className="h-px flex-1 bg-border-subtle" />
        </div>

        {/* The ONE shared control (`@givmo/design-system`); the testids stay this app's own contract —
            pinned by `auth-screens.test.tsx` (no Playwright spec pins them today; the e2e repo's
            `login-google-button` pin is the super-admin app's own door). */}
        <ProviderButton
          provider="google"
          label="Sign in with Google"
          loading={pendingProvider === 'google'}
          disabled={pendingProvider !== null || passkeyPending}
          onClick={() => void onProvider('google')}
          testId="login-google-button"
        />
        <ProviderButton
          provider="microsoft"
          label="Sign in with Microsoft"
          loading={pendingProvider === 'microsoft'}
          disabled={pendingProvider !== null || passkeyPending}
          onClick={() => void onProvider('microsoft')}
          testId="login-microsoft-button"
        />

        {passkeyAvailable && (
          <PasskeySignInButton
            loading={passkeyPending}
            disabled={pendingProvider !== null || passkeyPending}
            onClick={() => void onPasskey()}
          />
        )}
        {/* AS-WEB30: nothing at all where company sign-in can't run here (frame W0). */}
        <OrganizationSsoButton
          verb="signIn"
          available={ssoAvailable}
          disabled={pendingProvider !== null || passkeyPending}
          onOpen={openOrganizationSso}
        />
      </div>

      {/* AS-WEB27: the company lock's refusal is the notice with the way back in (frames B2, B3),
          never the red line; anything else the red line as before. */}
      <SignInDoorError
        error={error}
        lockRefused={lockRefused}
        onUnlink={openEscapeHatch}
        organizationSso={organizationSso}
      />

      {/* AS-WEB26: the way back in for someone their company locks out, under the ways in, only
          where a lock exists here (the link renders nothing anywhere else). It steps aside while
          the notice carries the way back in. */}
      {!(lockRefused && error !== null) && (
        <EscapeHatchEntryLink className={ESCAPE_HATCH_LINK_CONSOLE} onOpen={openEscapeHatch} />
      )}

      <p className="border-t border-border-subtle pt-20 text-center font-sans text-s14 font-w400 tracking-givmo text-text-secondary">
        Don’t have an account?{' '}
        <button
          type="button"
          onClick={() => void navigate({ to: AUTH_ROUTES.signUp })}
          className="font-w600 text-brand-600 outline-none transition-colors hover:text-brand-700 focus-visible:underline"
          {...testIdProp('auth-get-started')}
        >
          Get started
        </button>
      </p>
    </AuthShell>
  );
}
